<script setup>
import { computed } from "vue";

const props = defineProps({
  toast: {
    type: Object,
    required: true,
  },
});

const emit = defineEmits(["dismiss"]);

/*
 * Severity has to survive three different failure modes of the old library:
 * no colour, no icon, and no label. So each level carries all three — a solid
 * bar down the leading edge, a tinted icon tile, and an icon whose SHAPE
 * differs (triangle vs circle vs tick). Colour alone would leave the ~8% of
 * men with a colour-vision deficiency reading a red card as a grey one.
 *
 * Light mode tints the whole card; dark mode cannot (there is no `red-950` in
 * Tailwind 2.2's palette and a dark red panel would fight the gray-800 surface
 * every other card uses), so it leans on a saturated border plus the bar and
 * tile instead.
 */
const TONES = {
  error: {
    card: "bg-red-50 border-red-200 dark:bg-gray-800 dark:border-red-700",
    bar: "bg-red-600 dark:bg-red-500",
    tile: "bg-red-100 text-red-700 dark:bg-red-900 dark:text-red-200",
    badge: "bg-red-600 text-white dark:bg-red-500",
    action:
      "bg-red-600 text-white hover:bg-red-700 focus:ring-red-500 dark:bg-red-500 dark:hover:bg-red-400",
    label: "Error",
  },
  /* White text needs amber-700 (5.0:1). It sat on amber-500 (2.2:1) and, for
     the action, amber-600 (3.2:1) — both under WCAG AA. Same fix as the
     warning buttons in src/colors.js; the bar is decoration and stays 500. */
  warning: {
    card: "bg-amber-50 border-amber-200 dark:bg-gray-800 dark:border-amber-600",
    bar: "bg-amber-500",
    tile: "bg-amber-100 text-amber-700 dark:bg-amber-900 dark:text-amber-200",
    badge: "bg-amber-700 text-white",
    action:
      "bg-amber-700 text-white hover:bg-amber-800 focus:ring-amber-600",
    label: "Warning",
  },
  success: {
    card: "bg-green-50 border-green-200 dark:bg-gray-800 dark:border-green-600",
    bar: "bg-green-600 dark:bg-green-500",
    tile: "bg-green-100 text-green-700 dark:bg-green-900 dark:text-green-200",
    badge: "bg-green-600 text-white dark:bg-green-500",
    action:
      "bg-green-600 text-white hover:bg-green-700 focus:ring-green-500 dark:bg-green-500 dark:hover:bg-green-400",
    label: "Success",
  },
  info: {
    card: "bg-brand-50 border-brand-200 dark:bg-gray-800 dark:border-brand-600",
    bar: "bg-brand-600 dark:bg-brand-500",
    tile: "bg-brand-100 text-brand-700 dark:bg-brand-900 dark:text-brand-200",
    badge: "bg-brand-600 text-white dark:bg-brand-500",
    action:
      "bg-brand-600 text-white hover:bg-brand-700 focus:ring-brand-500 dark:bg-brand-500 dark:hover:bg-brand-400",
    label: "Information",
  },
};

const tone = computed(() => TONES[props.toast.severity] || TONES.info);

/*
 * `role="alert"` on the item itself, not a live region wrapping the whole
 * stack: a permanent region around a list that gains and loses children would
 * re-announce surviving siblings every time one auto-dismissed. An element
 * inserted WITH role="alert" already announces exactly once.
 *
 * Failures interrupt (assertive); confirmations wait their turn (polite).
 */
const isUrgent = computed(
  () => props.toast.severity === "error" || props.toast.severity === "warning"
);

const dismiss = () => emit("dismiss", props.toast.id);

const runAction = () => {
  const action = props.toast.action;
  if (!action) return;
  action.onClick();
  // A Retry that stays on screen looks like it did nothing. The retry either
  // succeeds (silence is the right answer) or fails and posts a fresh toast.
  if (!action.keepOpen) dismiss();
};
</script>

<template>
  <div
    :data-toast-id="toast.id"
    :role="isUrgent ? 'alert' : 'status'"
    :aria-live="isUrgent ? 'assertive' : 'polite'"
    aria-atomic="true"
    class="toast-card pointer-events-auto relative w-full overflow-hidden rounded-xl border shadow-card cursor-pointer"
    :class="tone.card"
    @click="dismiss"
  >
    <!-- The severity bar. Decorative: the icon tile and the title carry the
         same information to a screen reader. -->
    <span
      class="absolute inset-y-0 left-0 w-1"
      :class="tone.bar"
      aria-hidden="true"
    ></span>

    <div class="flex items-start gap-3 py-3 pl-4 pr-2">
      <span
        class="mt-px flex-shrink-0 inline-flex items-center justify-center h-8 w-8 rounded-lg"
        :class="tone.tile"
      >
        <!-- Shape differs per severity so severity survives greyscale. -->
        <svg
          v-if="toast.severity === 'error'"
          class="h-5 w-5"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          stroke-width="2"
          stroke-linecap="round"
          stroke-linejoin="round"
          aria-hidden="true"
        >
          <circle cx="12" cy="12" r="10" />
          <line x1="15" y1="9" x2="9" y2="15" />
          <line x1="9" y1="9" x2="15" y2="15" />
        </svg>
        <svg
          v-else-if="toast.severity === 'warning'"
          class="h-5 w-5"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          stroke-width="2"
          stroke-linecap="round"
          stroke-linejoin="round"
          aria-hidden="true"
        >
          <path d="M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0Z" />
          <line x1="12" y1="9" x2="12" y2="13" />
          <line x1="12" y1="17" x2="12.01" y2="17" />
        </svg>
        <svg
          v-else-if="toast.severity === 'success'"
          class="h-5 w-5"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          stroke-width="2"
          stroke-linecap="round"
          stroke-linejoin="round"
          aria-hidden="true"
        >
          <path d="M21.8 10A10 10 0 1 1 17 3.34" />
          <polyline points="9 11 12 14 22 4" />
        </svg>
        <svg
          v-else
          class="h-5 w-5"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          stroke-width="2"
          stroke-linecap="round"
          stroke-linejoin="round"
          aria-hidden="true"
        >
          <circle cx="12" cy="12" r="10" />
          <line x1="12" y1="16" x2="12" y2="12" />
          <line x1="12" y1="8" x2="12.01" y2="8" />
        </svg>
      </span>

      <div class="min-w-0 flex-1 pt-0.5">
        <p class="flex items-start gap-2 text-sm font-semibold leading-snug text-gray-900 dark:text-gray-50">
          <!-- The severity word is for screen readers only; sighted users get
               it from the colour, the bar and the icon shape. -->
          <span class="sr-only">{{ tone.label }}:</span>
          <span class="min-w-0 break-words">{{ toast.title }}</span>

          <!-- Collapsed duplicates. The badge is the ONLY thing that changes on
               a repeat, so a burst of identical failures cannot bury the page. -->
          <span
            v-if="toast.count > 1"
            class="flex-shrink-0 inline-flex items-center rounded-full px-2 py-0.5 text-2xs font-semibold leading-4"
            :class="tone.badge"
          >
            <span aria-hidden="true">&times;{{ toast.count }}</span>
            <span class="sr-only">happened {{ toast.count }} times</span>
          </span>
        </p>

        <p
          v-if="toast.detail"
          class="mt-1 text-xs leading-relaxed text-gray-600 dark:text-gray-300 break-words"
        >
          {{ toast.detail }}
        </p>

        <!-- The point of the whole rebuild: a failure that tells you what to do
             next. `.stop` so the action does not also trip the card's
             dismiss-on-click. -->
        <button
          v-if="toast.action"
          type="button"
          class="mt-2 inline-flex items-center justify-center min-h-[2.75rem] px-4 rounded-lg text-sm font-semibold shadow-card focus:outline-none focus:ring-2 focus:ring-offset-2 dark:focus:ring-offset-gray-800"
          :class="tone.action"
          @click.stop="runAction"
        >
          {{ toast.action.label }}
        </button>
      </div>

      <button
        type="button"
        class="flex-shrink-0 inline-flex items-center justify-center h-11 w-11 rounded-lg text-gray-400 hover:text-gray-700 hover:bg-black hover:bg-opacity-5 focus:outline-none focus:ring-2 focus:ring-brand-500 dark:text-gray-500 dark:hover:text-gray-100 dark:hover:bg-white dark:hover:bg-opacity-10"
        aria-label="Dismiss notification"
        @click.stop="dismiss"
      >
        <svg
          class="h-4 w-4"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          stroke-width="2.5"
          stroke-linecap="round"
          aria-hidden="true"
        >
          <line x1="18" y1="6" x2="6" y2="18" />
          <line x1="6" y1="6" x2="18" y2="18" />
        </svg>
      </button>
    </div>
  </div>
</template>
